import { useState, useEffect, useCallback } from 'react';
import { campaignApi } from '../services/api';
import type { 
  Campaign, 
  CampaignFilters, 
  CampaignListResponse,
  CampaignFormData 
} from '../types/campaign';

interface UseCampaignsState {
  campaigns: Campaign[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: CampaignFilters;
}

interface UseCampaignsReturn extends UseCampaignsState {
  fetchCampaigns: () => Promise<void>;
  createCampaign: (data: CampaignFormData) => Promise<Campaign>;
  updateCampaign: (id: string, data: Partial<CampaignFormData>) => Promise<Campaign>;
  deleteCampaign: (id: string) => Promise<void>;
  toggleCampaignStatus: (id: string) => Promise<Campaign>;
  setFilters: (filters: Partial<CampaignFilters>) => void;
  setPage: (page: number) => void;
  refreshCampaigns: () => Promise<void>;
}

export const useCampaigns = (initialFilters: CampaignFilters = {}): UseCampaignsReturn => {
  const [state, setState] = useState<UseCampaignsState>({
    campaigns: [],
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0
    },
    filters: {
      page: 1,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      includeMetrics: true,
      ...initialFilters
    }
  });

  const fetchCampaigns = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response: CampaignListResponse = await campaignApi.list(state.filters);
      
      // Handle API response format: { success: true, data: { campaigns: [...], pagination: {...} } }
      const responseData = (response as any).data || response;
      const campaigns = responseData.campaigns || [];
      const pagination = responseData.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
      };
      
      setState(prev => ({
        ...prev,
        campaigns,
        pagination,
        loading: false
      }));
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to fetch campaigns',
        loading: false
      }));
    }
  }, [state.filters]);

  const createCampaign = useCallback(async (data: CampaignFormData): Promise<Campaign> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await campaignApi.create(data);
      
      // Handle API response format: { success: true, data: campaign }
      const newCampaign = (response as any).data || response;
      if (newCampaign) {
        setState(prev => ({
          ...prev,
          campaigns: [newCampaign, ...prev.campaigns],
          pagination: {
            ...prev.pagination,
            total: prev.pagination.total + 1
          },
          loading: false
        }));
        
        return newCampaign;
      }
      throw new Error('No campaign data returned');
    } catch (error) {
      console.error('Failed to create campaign:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to create campaign',
        loading: false
      }));
      throw error;
    }
  }, []);

  const updateCampaign = useCallback(async (id: string, data: Partial<CampaignFormData>): Promise<Campaign> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await campaignApi.update(id, data);
      
      // Handle API response format: { success: true, data: campaign }
      const updatedCampaign = (response as any).data || response;
      if (updatedCampaign) {
        setState(prev => ({
          ...prev,
          campaigns: prev.campaigns.map(campaign => 
            campaign.id === id ? { ...campaign, ...updatedCampaign } : campaign
          ),
          loading: false
        }));
        
        return updatedCampaign;
      }
      throw new Error('No campaign data returned');
    } catch (error) {
      console.error('Failed to update campaign:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update campaign',
        loading: false
      }));
      throw error;
    }
  }, []);

  const deleteCampaign = useCallback(async (id: string): Promise<void> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await campaignApi.delete(id);
      
      // Optimistically remove from campaigns list
      setState(prev => ({
        ...prev,
        campaigns: prev.campaigns.filter(campaign => campaign.id !== id),
        pagination: {
          ...prev.pagination,
          total: prev.pagination.total - 1
        },
        loading: false
      }));
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to delete campaign',
        loading: false
      }));
      throw error;
    }
  }, []);

  const toggleCampaignStatus = useCallback(async (id: string): Promise<Campaign> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await campaignApi.toggleStatus(id);
      
      // Handle API response format: { success: true, data: campaign }
      const updatedCampaign = (response as any).data || response;
      if (updatedCampaign) {
        setState(prev => ({
          ...prev,
          campaigns: prev.campaigns.map(campaign => 
            campaign.id === id ? { ...campaign, ...updatedCampaign } : campaign
          ),
          loading: false
        }));
        
        return updatedCampaign;
      }
      throw new Error('No campaign data returned');
    } catch (error) {
      console.error('Failed to toggle campaign status:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to toggle campaign status',
        loading: false
      }));
      throw error;
    }
  }, []);

  const setFilters = useCallback((newFilters: Partial<CampaignFilters>) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        ...newFilters,
        page: newFilters.page || 1 // Reset to page 1 when filters change
      }
    }));
  }, []);

  const setPage = useCallback((page: number) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        page
      }
    }));
  }, []);

  const refreshCampaigns = useCallback(async () => {
    await fetchCampaigns();
  }, [fetchCampaigns]);

  // Fetch campaigns when filters change
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  return {
    ...state,
    fetchCampaigns,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    toggleCampaignStatus,
    setFilters,
    setPage,
    refreshCampaigns
  };
};

// Hook for single campaign management
export const useCampaign = (id: string) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaign = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await campaignApi.get(id);
      // Handle API response format: { success: true, data: campaign }
      const campaignData = (response as any).data || response;
      if (campaignData) {
        setCampaign(campaignData);
      }
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch campaign');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const validateCampaign = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await campaignApi.validate(id);
      // Handle API response format: { success: true, data: validation }
      const validationData = (response as any).data || response;
      setCampaign(prev => prev ? { ...prev, validation: validationData } : null);
      return validationData;
    } catch (error) {
      console.error('Failed to validate campaign:', error);
      setError(error instanceof Error ? error.message : 'Failed to validate campaign');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  return {
    campaign,
    loading,
    error,
    fetchCampaign,
    validateCampaign,
    refreshCampaign: fetchCampaign
  };
};

export default useCampaigns;